import { TextStyle, ViewStyle } from 'react-native';
import { tokens } from './tokens';

// Component Theme Configurations
// These define how components should look in different states and variants

export const componentThemes = {
  // Text Component Themes
  text: {
    variants: {
      // Normal variants
      'xxxl-normal': {
        fontSize: tokens.typography.fontSizes.xxxl,
        lineHeight: tokens.typography.lineHeights.xxxl,
        fontFamily: tokens.typography.fontFamilies.regular,
      },
      'xxl-normal': {
        fontSize: tokens.typography.fontSizes.xxl,
        lineHeight: tokens.typography.lineHeights.xxl,
        fontFamily: tokens.typography.fontFamilies.regular,
      },
      'xl-normal': {
        fontSize: tokens.typography.fontSizes.xl,
        lineHeight: tokens.typography.lineHeights.xl,
        fontFamily: tokens.typography.fontFamilies.regular,
      },
      'lg-normal': {
        fontSize: tokens.typography.fontSizes.lg,
        lineHeight: tokens.typography.lineHeights.lg,
        fontFamily: tokens.typography.fontFamilies.regular,
      },
      'md-normal': {
        fontSize: tokens.typography.fontSizes.md,
        lineHeight: tokens.typography.lineHeights.md,
        fontFamily: tokens.typography.fontFamilies.regular,
      },
      'sm-normal': {
        fontSize: tokens.typography.fontSizes.sm,
        lineHeight: tokens.typography.lineHeights.sm,
        fontFamily: tokens.typography.fontFamilies.regular,
      },
      'xsm-normal': {
        fontSize: tokens.typography.fontSizes.xsm,
        lineHeight: tokens.typography.lineHeights.xsm,
        fontFamily: tokens.typography.fontFamilies.regular,
      },
      // Bold variants
      'xxxl-bold': {
        fontSize: tokens.typography.fontSizes.xxxl,
        lineHeight: tokens.typography.lineHeights.xxxl,
        fontFamily: tokens.typography.fontFamilies.bold,
      },
      'xxl-bold': {
        fontSize: tokens.typography.fontSizes.xxl,
        lineHeight: tokens.typography.lineHeights.xxl,
        fontFamily: tokens.typography.fontFamilies.bold,
      },
      'xl-bold': {
        fontSize: tokens.typography.fontSizes.xl,
        lineHeight: tokens.typography.lineHeights.xl,
        fontFamily: tokens.typography.fontFamilies.bold,
      },
      'lg-bold': {
        fontSize: tokens.typography.fontSizes.lg,
        lineHeight: tokens.typography.lineHeights.lg,
        fontFamily: tokens.typography.fontFamilies.bold,
      },
      'md-bold': {
        fontSize: tokens.typography.fontSizes.md,
        lineHeight: tokens.typography.lineHeights.md,
        fontFamily: tokens.typography.fontFamilies.bold,
      },
      'sm-bold': {
        fontSize: tokens.typography.fontSizes.sm,
        lineHeight: tokens.typography.lineHeights.sm,
        fontFamily: tokens.typography.fontFamilies.bold,
      },
      'xsm-bold': {
        fontSize: tokens.typography.fontSizes.xsm,
        lineHeight: tokens.typography.lineHeights.xsm,
        fontFamily: tokens.typography.fontFamilies.bold,
      },
      // Special variants
      disabled: {
        color: tokens.colors.neutral.cotton,
      },
    },
    sizes: {
      xxxl: {
        fontSize: tokens.typography.fontSizes.xxxl,
        lineHeight: tokens.typography.lineHeights.xxxl,
      },
      xxl: {
        fontSize: tokens.typography.fontSizes.xxl,
        lineHeight: tokens.typography.lineHeights.xxl,
      },
      xl: {
        fontSize: tokens.typography.fontSizes.xl,
        lineHeight: tokens.typography.lineHeights.xl,
      },
      lg: {
        fontSize: tokens.typography.fontSizes.lg,
        lineHeight: tokens.typography.lineHeights.lg,
      },
      md: {
        fontSize: tokens.typography.fontSizes.md,
        lineHeight: tokens.typography.lineHeights.md,
      },
      sm: {
        fontSize: tokens.typography.fontSizes.sm,
        lineHeight: tokens.typography.lineHeights.sm,
      },
      xsm: {
        fontSize: tokens.typography.fontSizes.xsm,
        lineHeight: tokens.typography.lineHeights.xsm,
      },
    },
  },

  // Button Component Themes
  button: {
    variants: {
      solid: {
        base: {
          backgroundColor: tokens.colors.primary.carnation,
          borderColor: 'transparent',
          borderWidth: 0,
          borderRadius: tokens.borderRadius.full,
          alignItems: 'center' as const,
          justifyContent: 'center' as const,
          // Remove default padding - let size theme handle it
        },
        text: {
          color: tokens.colors.neutral.cotton,
          fontFamily: tokens.typography.fontFamilies.regular,
          fontSize: tokens.typography.fontSizes.md,
          fontWeight: '200',
        },
        pressed: {
          backgroundColor: tokens.colors.primary.darkCarnation,
        },
        disabled: {
          backgroundColor: tokens.colors.disabled.smoke,
          opacity: 0.5,
        },
        disabledText: {
          color: tokens.colors.neutral.darkMist,
        },
      },
      ghost: {
        base: {
          backgroundColor: tokens.colors.neutral.cotton,
          borderColor: tokens.colors.primary.carnation,
          borderWidth: tokens.borderWidth.thin,
          borderRadius: tokens.borderRadius.full,
          alignItems: 'center' as const,
          justifyContent: 'center' as const,
          // Remove default padding - let size theme handle it
        },
        text: {
          color: tokens.colors.primary.carnation,
          fontFamily: tokens.typography.fontFamilies.regular,
          fontSize: tokens.typography.fontSizes.md,
          fontWeight: '200',
        },
        pressed: {
          backgroundColor: tokens.colors.neutral.mist,
        },
        disabled: {
          backgroundColor: tokens.colors.neutral.mist,
          borderColor: tokens.colors.neutral.darkMist,
          opacity: 0.6,
        },
        disabledText: {
          color: tokens.colors.neutral.darkMist,
        },
      },
      group: {
        base: {
          backgroundColor: tokens.colors.neutral.cotton,
          borderColor: 'transparent',
          borderWidth: 0,
          borderRadius: tokens.borderRadius.md,
          alignItems: 'center' as const,
          justifyContent: 'center' as const,
          opacity: 0.9,
          // Remove default padding - let size theme handle it
        },
        text: {
          color: tokens.colors.neutral.charcoal,
          fontFamily: tokens.typography.fontFamilies.regular,
          fontSize: tokens.typography.fontSizes.sm,
          fontWeight: '200',
        },
        pressed: {
          backgroundColor: tokens.colors.neutral.charcoal,
        },
        disabled: {
          backgroundColor: tokens.colors.neutral.mist,
          opacity: 0.6,
        },
        disabledText: {
          color: tokens.colors.neutral.darkMist,
        },
      },
    },
    sizes: {
      sm: {
        paddingHorizontal: tokens.spacing.md,
        paddingVertical: tokens.spacing.sm,
        minHeight: 36,
      },
      md: {
        paddingHorizontal: tokens.spacing.lg,
        paddingVertical: tokens.spacing.md,
        minHeight: 48,
      },
      lg: {
        paddingHorizontal: tokens.spacing.xl + tokens.spacing.xs, // 32px + 4px = 36px
        paddingVertical: tokens.spacing.lg + tokens.spacing.xs, // 24px + 4px = 28px
        minHeight: 56,
      },
    },
  },

  // Input Component Themes
  input: {
    base: {
      borderColor: tokens.colors.neutral.charcoal,
      borderWidth: tokens.borderWidth.thin,
      borderRadius: tokens.borderRadius.md,
      backgroundColor: tokens.colors.neutral.cotton,
      paddingHorizontal: tokens.spacing.md,
      paddingVertical: tokens.spacing.md,
      minHeight: 48,
      fontSize: tokens.typography.fontSizes.md,
      fontFamily: tokens.typography.fontFamilies.regular,
      color: tokens.colors.neutral.charcoal,
    },
    focused: {
      borderColor: tokens.colors.secondary.orchid,
      borderWidth: tokens.borderWidth.medium,
    },
    error: {
      borderColor: tokens.colors.error.rose,
      borderWidth: tokens.borderWidth.medium,
    },
    disabled: {
      backgroundColor: tokens.colors.disabled.smoke,
      color: tokens.colors.neutral.darkMist,
    },
  },

  // Modal Component Themes
  modal: {
    overlay: {
      backgroundColor: tokens.colors.black,
      opacity: 0.8,
      flex: 1,
      justifyContent: 'center' as const,
      alignItems: 'center' as const,
    },
    content: {
      backgroundColor: tokens.colors.neutral.cotton,
      borderRadius: tokens.borderRadius.lg,
      padding: tokens.spacing.lg,
      margin: tokens.spacing.lg,
      maxWidth: '90%',
      maxHeight: '90%',
    },
    header: {
      borderBottomWidth: tokens.borderWidth.thin,
      borderBottomColor: tokens.colors.neutral.mist,
      paddingBottom: tokens.spacing.md,
      marginBottom: tokens.spacing.md,
    },
    title: {
      fontSize: tokens.typography.fontSizes.lg,
      fontFamily: tokens.typography.fontFamilies.bold,
      color: tokens.colors.neutral.charcoal,
    },
    body: {
      paddingVertical: tokens.spacing.md,
    },
    footer: {
      borderTopWidth: tokens.borderWidth.thin,
      borderTopColor: tokens.colors.neutral.mist,
      paddingTop: tokens.spacing.md,
      marginTop: tokens.spacing.md,
      flexDirection: 'row' as const,
      justifyContent: 'flex-end' as const,
      gap: tokens.spacing.sm,
    },
  },

  // CheckBox Component Themes
  checkbox: {
    base: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      gap: tokens.spacing.sm,
    },
    checkbox: {
      width: 20,
      height: 20,
      borderWidth: tokens.borderWidth.thin,
      borderColor: tokens.colors.neutral.charcoal,
      borderRadius: tokens.borderRadius.sm,
      alignItems: 'center' as const,
      justifyContent: 'center' as const,
    },
    checked: {
      backgroundColor: tokens.colors.primary.carnation,
      borderColor: tokens.colors.primary.carnation,
    },
    disabled: {
      opacity: 0.5,
    },
    label: {
      fontSize: tokens.typography.fontSizes.md,
      fontFamily: tokens.typography.fontFamilies.regular,
      color: tokens.colors.neutral.charcoal,
    },
    disabledLabel: {
      color: tokens.colors.neutral.darkMist,
    },
  },

  // Toast Component Themes
  toast: {
    base: {
      position: 'absolute' as const,
      top: 0,
      left: 0,
      right: 0,
      zIndex: tokens.zIndex.toast,
    },
    error: {
      backgroundColor: tokens.colors.error.rose,
      padding: tokens.spacing.md,
      alignItems: 'center' as const,
    },
    success: {
      backgroundColor: tokens.colors.success.cactus,
      padding: tokens.spacing.md,
      alignItems: 'center' as const,
    },
    warning: {
      backgroundColor: tokens.colors.warning.sunflower,
      padding: tokens.spacing.md,
      alignItems: 'center' as const,
    },
    text: {
      color: tokens.colors.neutral.cotton,
      fontSize: tokens.typography.fontSizes.md,
      fontFamily: tokens.typography.fontFamilies.regular,
    },
  },
} as const;

// Type definitions
export type TextTheme = typeof componentThemes.text;
export type ButtonTheme = typeof componentThemes.button;
export type InputTheme = typeof componentThemes.input;
export type ModalTheme = typeof componentThemes.modal;
export type CheckBoxTheme = typeof componentThemes.checkbox;
export type ToastTheme = typeof componentThemes.toast;
